import { useCallback, useRef, useState } from "react";
import type { RefObject } from "react";

import { LinearGradient } from "expo-linear-gradient";
import isNil from "lodash/isNil";
import { useWindowDimensions } from "tamagui";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
  OnboardingAnalyticsEvent,
  OnboardingAnalyticsPageView,
} from "@/constants/analytics/Analytics";
import { useFocusEffect } from "@/crossPlatform/expo-router";
import { useAnalytics } from "@/services/Analytics";
import VideoPlayerCardControl from "@/ui/components/onboarding/welcome/videoPlayerCard/VideoPlayerCardControls";
import type {
  ExpoVideoPlayerHandle,
  VideoPlaybackStatus,
} from "@/ui/composed/media/videoPlayer/adapter/ExpoVideoPlayer";
import VideoPlayer from "@/ui/composed/media/videoPlayer/VideoPlayer";
import { Box } from "@/ui/layout";
import { WebContentMaxWidth } from "@/ui/layout/ScreenContainer";
import { LayoutToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

type VideoPlayerHandle = ExpoVideoPlayerHandle;

interface VideoPlayerCardProps extends ViewWithTestID {
  onError: (error: string) => void;
  thumbnail?: string;
  title?: string;
  videoRef?: RefObject<null | VideoPlayerHandle>;
  videoUrl: string;
}

// These values are not taken from figma, they are hardcoded values to maintain the same design as figma
// due to the fact that we are using video player which makes it difficult to maintain the values to achieve the same design as figma
const VideoPlayerStyles = {
  borderBottomLeftRadius: 96,
  borderBottomRightRadius: 33,
  borderTopLeftRadius: 30,
  borderTopRightRadius: 98,
};

const VideoPlayerCard = ({
  onError,
  testID,
  thumbnail,
  title,
  videoRef: externalVideoRef,
  videoUrl,
}: VideoPlayerCardProps) => {
  const { track } = useAnalytics();

  // If externalVideoRef is provided then use it else we use internalVideoRef
  // This is needed to control the video from parent component if needed (e.g. pause the video when user navigates away from the screen)
  // For context look at JN-1213
  const internalVideoRef = useRef<null | VideoPlayerHandle>(null);
  const videoRef = externalVideoRef || internalVideoRef;
  const { width } = useWindowDimensions();
  const [isPlaying, setIsPlaying] = useState(false);

  // according to figma aspect ratio of video is 204/315 = 0.647, where width is 315 and height is 240 (240/315 = 0.647)
  // so we need to calculate height based on width and aspect ratio of video to maintain the aspect ratio of video
  // we have LayoutToken.OnboardingScreenHorizontalPadding padding on both sides of video so we need to subtract that from width to get actual width of video

  // on web, ScreenContainer caps content at WebContentMaxWidth, so the card's real width stops growing there even as the window widens.
  // useWindowDimensions().width keeps climbing past that cap, so clamp to the container width before the aspect-ratio calc or the height over-stretches. Native is unaffected (window width is the card width).
  const cardWidth = isWeb() ? Math.min(width, WebContentMaxWidth) : width;
  const videoHeight =
    (cardWidth - 2 * LayoutToken.OnboardingScreenHorizontalPadding) * 0.647;

  const handleVideoPress = async () => {
    track(OnboardingAnalyticsEvent.VideoPlayerCardPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Card,
      [AnalyticsFieldName.Source]: OnboardingAnalyticsPageView.Welcome,
    });

    if (isNil(videoRef.current)) return;

    const status: VideoPlaybackStatus = await videoRef.current.getStatusAsync();

    // if video is not playing then play the video else pause the video
    if ("isPlaying" in status && !status.isPlaying) {
      void videoRef.current.playAsync();
      setIsPlaying(true);
    } else {
      void videoRef.current.pauseAsync();
      setIsPlaying(false);
    }
  };
  // when user navigates away from the screen then pause the video
  useFocusEffect(
    useCallback(() => {
      const video = videoRef.current;
      return () => {
        // if video is not loaded or video is not playing then return
        if (!video || !isPlaying) return;
        setIsPlaying(false);
      };
    }, [isPlaying, videoRef]),
  );

  return (
    <Box
      alignItems="center"
      height={videoHeight}
      justifyContent="center"
      position="relative"
      width="100%"
    >
      <VideoPlayer
        onError={onError}
        ref={videoRef}
        resizeMode="cover"
        shouldAutoPlay={false}
        shouldShowControls={false}
        shouldShowThumbnail
        size={{ height: "100%", width: "100%" }}
        styles={{
          borderBottomLeftRadius: VideoPlayerStyles.borderBottomLeftRadius,
          borderBottomRightRadius: VideoPlayerStyles.borderBottomRightRadius,
          borderTopLeftRadius: VideoPlayerStyles.borderTopLeftRadius,
          borderTopRightRadius: VideoPlayerStyles.borderTopRightRadius,
        }}
        testID={testID}
        thumbnailUrl={thumbnail}
        title={title}
        videoUrl={videoUrl}
      />
      <Box
        bottom={0}
        onPress={() => void handleVideoPress()}
        position="absolute"
        width="100%"
      >
        <LinearGradient
          colors={[
            "rgba(34, 102, 255, .5)",
            "rgba(68, 124, 254, 0.01)",
            "transparent",
          ]}
          end={{ x: 1, y: 0 }}
          locations={[0, 0.3, 1]}
          start={{ x: 0.8, y: 1 }}
          style={{
            alignItems: "center",
            borderBottomLeftRadius: VideoPlayerStyles.borderBottomLeftRadius,
            borderRadius: VideoPlayerStyles.borderTopLeftRadius,
            bottom: 0,
            height: videoHeight,
            justifyContent: "center",
            position: "absolute",
            shadowColor: "#000000",
            shadowOffset: { height: 1, width: 0 },
            shadowOpacity: 0.06,
            shadowRadius: 4,
            width: "100%",
          }}
        />
        <VideoPlayerCardControl
          isPlaying={isPlaying}
          onControlPress={handleVideoPress}
          testID={joinTestIDs(testID, "controls")}
          title={title}
        />
      </Box>
    </Box>
  );
};
export type { VideoPlayerCardProps, VideoPlayerHandle };
export default VideoPlayerCard;
